import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useContext } from 'react';
import { useSnackbar } from 'notistack';
import AuthContext from '../../store/auth-context';
// eslint-disable-next-line no-unused-vars
import { getCarDetails, getTranslatedAuctionSheet, requestInspection, updateCarFavourite, getCarMarketStats, getAuctionHistory } from "../../services";
import { sendInspectionRequest, getUserInspections, createBid, getUserBids, sendBidNotification } from "../../services";
import { updateCarDetail, setCarDetail, checkFavoriteStatus } from '../../store/reducers/carReducer';
import InspectionGallery from './InspectionGallery';
import TranslatedContent from './TranslatedContent';
import ProductionDateCard from './ProductionDateCard';
import AuctionHistoryCard from './AuctionHistoryCard';
import EndedAuctionBanner, { isAuctionOver } from './EndedAuctionBanner';
import classes from './DetailContent.module.scss';
import { DateTime } from 'luxon';
import VIPFeatureIndicator from '../UI/VIPFeatureIndicator';
import UpgradeModal from '../UI/UpgradeModal';
import { useDisclosure } from '@chakra-ui/react';
import { EXCLUDED_MAKERS } from '../../config/constants';
import axiosInterceptors from "../../services/axiosHandler";
import { baseURL } from "../../services";

// Where the Back control goes when there is nothing safe to go back to.
const DEFAULT_BACK_PATH = '/auction';

// The `returnTo` query parameter comes straight from the address bar, so it is
// attacker-controlled: a link to our own origin carrying
// `?returnTo=https://evil.example/login` would bounce the visitor off-site on
// the Back click (open redirect). Only a same-origin, root-relative path is
// accepted; everything else falls back to the default route.
const sanitizeReturnPath = (rawPath) => {
  if (typeof rawPath !== 'string') return null;

  const candidate = rawPath.trim();
  if (!candidate) return null;

  // Whitespace and control characters are stripped by browsers when resolving a
  // URL, so "/\tjavascript:alert(1)" must never reach the navigation call.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0020\u007F]/.test(candidate)) return null;

  // Must be a root-relative path...
  if (!candidate.startsWith('/')) return null;

  // ...and must not be a protocol-relative reference ("//evil.example/x") or use
  // a backslash, which several browsers normalise to a forward slash.
  if (candidate.startsWith('//') || candidate.includes('\\')) return null;

  // Belt and braces: reject anything carrying a URL scheme.
  if (/^[a-z][a-z0-9+.-]*:/i.test(candidate)) return null;

  return candidate;
};

// Starting Bid is a paid (VIP+) field. It is enforced server-side, so for a
// lower tier the field is simply not in the response - and a car record can
// legitimately have no starting bid at all. Reduce whatever arrives to a string
// React can render, or null when there is nothing to show.
const readStartingBid = (details) => {
  const raw = details?.bidDetail?.start || details?.carDetail?.vehicleDetailBtmLine;
  if (raw === undefined || raw === null || typeof raw === 'object') return null;
  const text = String(raw).trim();
  return text ? text : null;
};

const DetailContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { userData, isFreeTier, isVipPlus } = useContext(AuthContext);
  const [currentImage, setCurrentImage] = useState(null);
  const [carDetails, setCarDetails] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loadingForTranslatedAuction, setLoadingForTranslatedAuction] = useState(false);
  const [loadingForCarDetails, setLoadingForCarDetails] = useState(true);
  const carDetail = useSelector((state) => state.car.detail);
  const [showTranslated, setShowTranslated] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const translatedContentRef = useRef(null);
  const [inspection, setInspection] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const inspectionGalleryRef = useRef(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoadingInspection, setIsLoadingInspection] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Check if user is admin
  const isAdmin = userData?.userRole === "administrator";

  // Paid field - may be absent from the payload for a non-VIP+ account.
  const canSeeStartingBid = isVipPlus || isAdmin;
  const startingBid = readStartingBid(carDetails);
  const reloadedForUpgrade = useRef(null);

  // The API holds back a free account's photos after the first; photoCount says how many
  // there are in all. Paid members have no photoCount, and every photo is in vehicleImages.
  const loadedPhotoCount = carDetails?.vehicleImages?.length || 0;
  const totalPhotoCount = Math.max(carDetails?.photoCount || 0, loadedPhotoCount);
  const lockedPhotoCount = totalPhotoCount - loadedPhotoCount;
  const reloadedAfterUpgrade = useRef(null);

  // Bidding feature states
  const [bidAmount, setBidAmount] = useState('');
  const [shippedPrice, setShippedPrice] = useState(0);
  const [importedPrice, setImportedPrice] = useState(0);
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  const [bidPlaced, setBidPlaced] = useState(false);
  const [showShippedDetails, setShowShippedDetails] = useState(false);
  const [showImportedDetails, setShowImportedDetails] = useState(false);
  const [isBiddingExpanded, setIsBiddingExpanded] = useState(false);
  // Add state for bid confirmation dialog
  const [showBidConfirmation, setShowBidConfirmation] = useState(false);
  // Add state for inspection confirmation dialog
  const [showInspectionConfirmation, setShowInspectionConfirmation] = useState(false);
  // Add state to track minimum bid amount (last successful bid)
  const [minimumBid, setMinimumBid] = useState(0);
  const [existingBid, setExistingBid] = useState(null);
  const [bidError, setBidError] = useState('');
  
  // Constants for fee calculations - Shipping
  const FREIGHT_SHIPPING = 1950;
  const JAPAN_INLAND_TRANSPORT = 400;
  const EXPORT_FEE = 500;
  const MAGIC_MOTORSPORTS_FEE = 1000;
  
  // Constants for fee calculations - Imported
  const US_IMPORT_TARIFF_RATE = 0.125; // 12.5%
  const HARBOR_MAINTENANCE_FEE_RATE = 0.00125; // 0.0125%
  const PROCESSING_FEE_RATE = 0.00346; // 0.346%
  const TERMINAL_HANDLING_FEE = 177.75;
  const IMPORT_FEE = 350;
  
  // Create a new state for the VIP photos modal
  const { isOpen: isUpgradeModalOpen, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
  const dispatch = useDispatch();
  
  const [localFavorites, setLocalFavorites] = useState(new Set());
  const [marketStats, setMarketStats] = useState(null);
  const [auctionHistory, setAuctionHistory] = useState(null);
  const [loadingAuctionHistory, setLoadingAuctionHistory] = useState(false);
  const [loadingMarketStats, setLoadingMarketStats] = useState(false);
  
  // Define displaySnackbar function using useCallback to prevent dependency changes
  const displaySnackbar = useCallback((message, variant) => {
    enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "right",
      },
    });
  }, [enqueueSnackbar]);
  
  // Initial effect to check localStorage for favorite status when component mounts
  useEffect(() => {
    if (id) {
      try {
        // Check localStorage for favorite status as soon as we have the ID
        const savedIds = localStorage.getItem('favoriteCarIds');
        if (savedIds) {
          const favoriteIds = JSON.parse(savedIds);
          const isFavoritedInStorage = favoriteIds.includes(id);
          
          // This is a first-pass check before we have any other data
          console.log(`Initial favorite check from localStorage for ID ${id}: ${isFavoritedInStorage}`);
          
          // Initialize the isFavorite state based on localStorage
          // This ensures we have the right status even before API/Redux data loads
          if (isFavoritedInStorage) {
            setIsFavorite(true);
          }
        }
      } catch (error) {
        console.error("Error checking initial favorite status:", error);
      }
    }
  }, [id]);
  
  // Effect for favorite status synchronization with localStorage
  useEffect(() => {
    // This effect supplements favorite status from localStorage.
    // IMPORTANT: The server/Redux value is authoritative. localStorage may only
    // ADD a favorite the server hasn't reported yet (true) — it must never flip a
    // server 'true' to 'false' just because the id is absent from storage, since
    // that would make the next heart click invert the true server state.
    if (carDetails?._id) {
      try {
        // Check if this car is in the favorite IDs stored in localStorage
        const savedIds = localStorage.getItem('favoriteCarIds');
        if (savedIds) {
          const favoriteIds = JSON.parse(savedIds);
          const isFavoritedInStorage = favoriteIds.includes(carDetails._id);

          console.log("Favorite status check:", {
            carId: carDetails._id,
            isFavoriteInState: isFavorite,
            isFavoritedInStorage: isFavoritedInStorage,
            isFavoriteInRedux: carDetail?.isFavorite
          });

          // Only supplement: if localStorage marks this car as favorite but the
          // current state doesn't, promote it to favorite. Never do the reverse.
          if (isFavoritedInStorage && !isFavorite) {
            console.log("Supplementing favorite status from localStorage: true");
            setIsFavorite(true);

            // Also dispatch to Redux to keep everything in sync
            dispatch(updateCarDetail({
              _id: carDetails._id,
              isFavorite: true
            }));
          }
        }
      } catch (error) {
        console.error("Error syncing favorite status from localStorage:", error);
      }
    }
  }, [carDetails, isFavorite, dispatch, carDetail]);
  
  // Calculate prices whenever bid amount changes
  useEffect(() => {
    if (bidAmount && !isNaN(bidAmount)) {
      const bidValue = parseFloat(bidAmount);
      
      // Calculate shipped price
      const totalShipped = bidValue + FREIGHT_SHIPPING + JAPAN_INLAND_TRANSPORT + EXPORT_FEE + MAGIC_MOTORSPORTS_FEE;
      setShippedPrice(totalShipped);
      
      // Calculate imported price
      const importTariff = bidValue * US_IMPORT_TARIFF_RATE;
      const harborFee = bidValue * HARBOR_MAINTENANCE_FEE_RATE;
      const processingFeeRate = bidValue * PROCESSING_FEE_RATE;
      const totalImported = totalShipped + importTariff + harborFee + processingFeeRate + TERMINAL_HANDLING_FEE + IMPORT_FEE;
      
      setImportedPrice(totalImported);
    } else {
      setShippedPrice(0);
      setImportedPrice(0);
    }
  }, [bidAmount]);
  
  // Handle bid input change
  const handleBidChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === '' || (/^\d*\.?\d*$/).test(value)) {
      // Allow typing any valid number regardless of minimum bid
      setBidAmount(value);
    }
  };
  
  // Handle place bid action
  const handlePlaceBid = () => {
    setBidError('');
    const numValue = parseFloat(bidAmount) || 0;
    
    if (!userData || !userData.userId) {
      displaySnackbar("Please log in to place a bid", "error");
      return;
    }
    
    if (isFreeTier) {
      onOpenUpgradeModal();
      return;
    }
    
    if (!bidAmount || isNaN(bidAmount) || numValue <= 0) {
      setBidError("Please enter a valid bid amount");
      displaySnackbar("Please enter a valid bid amount", "error");
      return;
    }
    
    // If there's an existing bid, ensure the new bid is higher
    if (bidPlaced && numValue <= minimumBid) {
      const formattedMinBid = (minimumBid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setBidError(`Your new bid must be higher than your current bid of $${formattedMinBid}`);
      displaySnackbar(`Your new bid must be higher than your current bid of $${formattedMinBid}`, "error");
      return;
    }
    
    // Check if bid exceeds deposit limit (10x deposit)
    const depositAmount = (userData.deposit?.amount || 0);
    const depositLimit = depositAmount * 10;
    const formattedDepositLimit = depositLimit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    if (numValue > depositLimit) {
      setBidError(`Your bid exceeds your limit of $${formattedDepositLimit} (10x your deposit)`);
      displaySnackbar(`Your bid exceeds your limit of $${formattedDepositLimit} (10x your deposit)`, "error");
      return;
    }
    
    // Show confirmation dialog
    setShowBidConfirmation(true);
  };
  
  // Handle bid confirmation
  const confirmBid = async () => {
    setShowBidConfirmation(false);
    setIsPlacingBid(true);
    setBidError('');
    
    try {
      const bidData = {
        carId: carDetails._id,
        bidAmount: parseFloat(bidAmount),
        shippedPrice: shippedPrice,
        importedPrice: importedPrice,
        bidDate: new Date().toISOString()
      };
      
      const response = await createBid(bidData);
      
      if (response.message === "Success" || response.message === "Bid placed successfully") {
        // Update the local state with the new bid
        await fetchUserBids();
        
        // Send email notification to admin. The server builds the whole email from the
        // stored bid, so the only thing it accepts (and trusts) is the bid id.
        try {
          const bidId = response.data?.bidId;
          if (bidId) {
            await sendBidNotification({ bidId });
          } else {
            console.error("No bidId returned from createBid; skipping notification email");
          }
        } catch (emailError) {
          console.error("Error sending bid notification email:", emailError);
          // Don't show error to user since the bid was placed successfully
        }
        
        if (existingBid) {
          displaySnackbar("Your bid has been successfully increased!", "success");
        } else {
          displaySnackbar("Your bid has been placed successfully!", "success");
        }
      } else if (response?.upgradeRequired) {
        // The API reads the tier off the member's record, so this browser's is stale.
        onOpenUpgradeModal();
      } else {
        setBidError(response.message || "Failed to place bid");
        displaySnackbar(response.message || "Failed to place bid", "error");
      }
    } catch (error) {
      console.error("Error placing bid:", error);
      let errorMessage = "Error placing bid. Please try again.";
      
      if (error.response) {
        // Handle specific error messages from the API
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 403) {
          errorMessage = "You need to upgrade your account to place bids";
        } else if (error.response.status === 400) {
          errorMessage = "Invalid bid amount or missing information";
        }
      }
      
      setBidError(errorMessage);
      displaySnackbar(errorMessage, "error");
    } finally {
      setIsPlacingBid(false);
    }
  };
  
  // Cancel bid confirmation
  const cancelBidConfirmation = () => {
    setShowBidConfirmation(false);
  };
  
  // Toggle bidding section expansion
  const toggleBiddingSection = () => {
    setIsBiddingExpanded(!isBiddingExpanded);
  };
  
  // Toggle shipped details visibility
  const toggleShippedDetails = () => {
    setShowShippedDetails(!showShippedDetails);
  };

  // Toggle imported details visibility
  const toggleImportedDetails = () => {
    setShowImportedDetails(!showImportedDetails);
  };
  
  // Fix scroll jittering by disabling smooth scrolling behavior when the component mounts
  useEffect(() => {
    // Store the original scroll behavior
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    
    // Set scroll behavior to auto (disable smooth scrolling)
    document.documentElement.style.scrollBehavior = 'auto';
    
    // Force a small scroll to apply the behavior
    window.scrollTo(0, window.scrollY);
    
    // Cleanup function to restore original behavior when component unmounts
    return () => {
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
    };
  }, []);

  useEffect(() => {
    if (carDetail) {
      console.log("Car Details loaded from Redux: ", carDetail);
      if (location.state?.from === "/purchase") {
        // Use index 1 if available, otherwise use index 0
        setCurrentImage(carDetail?.vehicleImages?.length > 1 ? carDetail.vehicleImages[1] : carDetail?.vehicleImages?.[0]);
      } else {
        // Always use the first image in the array 
        setCurrentImage(carDetail?.vehicleImages?.[0]);
      }
      setCarDetails(carDetail);
      
      // Explicitly log the favorite status to help debug
      console.log("Initial favorite status from Redux:", carDetail.isFavorite);
      
      // Initialize favorite status based on car details, using strict boolean conversion
      // Use !! to ensure we get a boolean value from any truthy/falsy value
      setIsFavorite(!!carDetail.isFavorite);
      
      // Also initialize the localFavorites Set
      if (carDetail.isFavorite && carDetail._id) {
        setLocalFavorites(prev => {
          const newSet = new Set(prev);
          newSet.add(carDetail._id);
          return newSet;
        });
      }
    }
  }, [carDetail, location.state?.from]);

  const fetchUserInspections = useCallback(async () => {
    try {
      if (!carDetails?._id) return;

      const response = await getUserInspections(carDetails._id);
      if (response.data && response.data.length > 0) {
        setInspection(response.data[0]);
      } else {
        setInspection({});
      }
    } catch (error) {
      console.log('Error fetching inspection requests:', error);
      displaySnackbar("Failed to fetch inspection status", "error");
    }
  }, [carDetails?._id, displaySnackbar]);

  useEffect(() => {
    if (carDetails?._id) {
      fetchUserInspections();
    }
  }, [carDetails, fetchUserInspections]);

  const handleBackClick = () => {
    console.log("Location: ", location);
    
    // Check for returnTo parameter in URL which contains the previous page path.
    // The value is untrusted (it is whatever the address bar says), so it is
    // validated as a same-origin path before it is used - see sanitizeReturnPath.
    const params = new URLSearchParams(window.location.search);
    const returnPath = sanitizeReturnPath(params.get('returnTo'));

    if (returnPath) {
      // Navigate to the stored return path (previous page with its state).
      // navigate() is used rather than window.location.href so that even a value
      // that slipped past the check above can never leave this origin.
      navigate(returnPath);
    } else if (location.state?.from) {
      // Fall back to the old method if returnTo is not available
      navigate(location.state.from);
    } else {
      // Default to auction page if no return path is specified
      navigate(DEFAULT_BACK_PATH);
    }
  };

  const handleGetCarDetails = useCallback((id) => {
    // Add debugging to see what ID is being passed
    console.log("DetailPage: Fetching car details with ID:", id);
    
    // A car moves to the archive once its auction is over, and won cars are
    // linked from the purchase pipeline, the garage and customer emails - so
    // look there too. Bids on a closed auction are refused by the API.
    const payload = { id, checkBothCollections: true };
    getCarDetails(payload).then((res) => {
      // A car in neither collection comes back as Success with no data: show
      // "not found" rather than crash on it.
      if (res?.message === "Success" && res.data) {
        console.log("Car Details from API: ", res.data);
        // Log the car bid number for debugging
        console.log("Fetched car Bid No:", res?.data?.bidNo);
        
        // ------------------------------------------------------------------
        // Normalize vehicle images to ensure the array is always populated
        // ------------------------------------------------------------------
        // An auction URL with an empty FilePath param is the upstream's
        // "Now Printing" endpoint — the photo/auction sheet doesn't exist yet,
        // so the URL only renders the placeholder card. Never display it.
        const isPendingUpstreamUrl = (url) =>
          typeof url === "string" && /[?&]FilePath=(&|$)/.test(url);

        let imagesArray = Array.isArray(res?.data?.vehicleImages)
          ? res.data.vehicleImages.filter((url) => !isPendingUpstreamUrl(url))
          : [];

        // Fallback: some records only have images nested under carDetail.vehicleDetailImages
        if ((imagesArray.length === 0) && Array.isArray(res?.data?.carDetail?.vehicleDetailImages)) {
          imagesArray = [...res.data.carDetail.vehicleDetailImages];
        }

        // Last resort: our re-hosted copies were never created (the scraper's image
        // upload failed), so show the auction's own URLs rather than nothing — the
        // same source the auction sheet already falls back to below.
        if ((imagesArray.length === 0) && Array.isArray(res?.data?.carDetail?.orignalVehicleDetailImages)) {
          imagesArray = res.data.carDetail.orignalVehicleDetailImages.filter(
            (url) => typeof url === "string" && url.trim() && !isPendingUpstreamUrl(url)
          );
        }

        // Include the single vehicleDetailImage if present
        if (res?.data?.vehicleDetailImage && !isPendingUpstreamUrl(res.data.vehicleDetailImage)) {
          imagesArray.push(res.data.vehicleDetailImage);
        }

        // Include the auction sheet image if present - prioritize processed version.
        // A pending ("Now Printing") original is skipped entirely: the sheet will
        // appear on its own once the scraper picks up the published version.
        if (
          res?.data?.carDetail?.vehicleDetailImage &&
          res.data.carDetail.vehicleDetailImage.trim() &&
          res.data.carDetail.vehicleDetailImage !== "error" &&
          !isPendingUpstreamUrl(res.data.carDetail.vehicleDetailImage)
        ) {
          imagesArray.push(res.data.carDetail.vehicleDetailImage);
        } else if (
          res?.data?.carDetail?.orignalVehicleDetailImage &&
          res.data.carDetail.orignalVehicleDetailImage.trim() &&
          !isPendingUpstreamUrl(res.data.carDetail.orignalVehicleDetailImage)
        ) {
          imagesArray.push(res.data.carDetail.orignalVehicleDetailImage);
        }

        // Remove duplicates (if any) and assign back to the record
        res.data.vehicleImages = [...new Set(imagesArray)];
        // ------------------------------------------------------------------
        
        // Preserve favorite status from Redux if it exists
        // This ensures user changes from other pages are not overwritten
        const preservedFavoriteStatus = carDetail?.isFavorite;
        
        // Create an updated object that keeps the favorite status from Redux if available
        const updatedData = {
          ...res?.data,
          isFavorite: preservedFavoriteStatus !== undefined ? preservedFavoriteStatus : !!res?.data.isFavorite
        };
        
        // Ensure we update Redux with the complete car details including preserved favorite status
        dispatch(setCarDetail(updatedData));
        
        setCarDetails(updatedData);
        
        // Explicitly log the favorite status to help debug
        console.log("Initial favorite status from API:", res?.data.isFavorite);
        console.log("Preserved favorite status from Redux:", preservedFavoriteStatus);
        console.log("Final favorite status used:", updatedData.isFavorite);
        
        // Initialize favorite status using the preserved value - force to boolean with !!
        setIsFavorite(!!updatedData.isFavorite);
        
        // Also initialize localFavorites Set to include this car if it's marked as favorite
        if (updatedData.isFavorite === true) {
          setLocalFavorites(prev => {
            const newSet = new Set(prev);
            newSet.add(res?.data._id);
            return newSet;
          });
        }
        
        // Check if the car belongs to an excluded maker
        if (res?.data?.makerDetail?.maker && EXCLUDED_MAKERS.includes(res?.data?.makerDetail?.maker)) {
          console.log(`Redirecting: Car with maker ${res?.data?.makerDetail?.maker} is excluded`);
          navigate('/auction', { replace: true });
          displaySnackbar("This vehicle category is no longer available", "info");
          return;
        }
        
        if (res?.data?.vehicleImages?.length > 0) {
          setCurrentImage(res?.data?.vehicleImages[0]);
        }
        
        setLoadingForCarDetails(false);
      } else {
        setLoadingForCarDetails(false);
        setCarDetails(null);
      }
    });
  }, [dispatch, navigate, displaySnackbar, carDetail]);

  useEffect(() => {
    // Log ID from URL params
    console.log("DetailContent: ID from URL params:", id);
    console.log("DetailContent: carDetail from Redux:", carDetail?._id, carDetail?.bidNo);
    console.log("DetailContent: current favorite status in Redux:", carDetail?.isFavorite);
    
    if (id) {
      // A car loaded before the member upgraded still has its photos held back. Once per
      // car, in case the browser's tier is the stale one and the API holds them back again.
      const photosHeldBack = !isFreeTier && carDetail?.photoCount > (carDetail?.vehicleImages?.length || 0);
      const reloadForUpgrade = photosHeldBack && reloadedAfterUpgrade.current !== id;

      // If we have car details in Redux and the IDs match, use that first
      if (carDetail && carDetail._id === id && !reloadForUpgrade) {
        console.log("Using car details from Redux instead of making an API call");
        setCarDetails(carDetail);
        setCurrentImage(carDetail?.vehicleImages?.length > 0 ? carDetail?.vehicleImages?.[0] : null);
        
        // Force boolean conversion with !!
        const isFavoriteValue = !!carDetail.isFavorite;
        console.log("Setting favorite status from Redux to:", isFavoriteValue);
        setIsFavorite(isFavoriteValue);
        
        // Also update localFavorites for consistency
        if (isFavoriteValue) {
          setLocalFavorites(prev => {
            const newSet = new Set(prev);
            newSet.add(carDetail._id);
            return newSet;
          });
        }
        
        setLoadingForCarDetails(false);
      } else {
        // Otherwise make an API call
        if (reloadForUpgrade) reloadedAfterUpgrade.current = id;
        handleGetCarDetails(id);
      }
    }
  }, [id, carDetail, handleGetCarDetails, isFreeTier]);

  // A car loaded before the member upgraded still has its Auction Time held back. Once per
  // car, in case the browser's tier is the stale one and the API holds it back again.
  useEffect(() => {
    if (isFreeTier || carDetail?._id !== id || !carDetail?.tierLimited) return;
    if (reloadedForUpgrade.current === id) return;
    reloadedForUpgrade.current = id;
    handleGetCarDetails(id);
  }, [id, carDetail, isFreeTier, handleGetCarDetails]);

  useEffect(() => {
    if (!carDetails?._id || isFreeTier) return;

    const inspectionScore = carDetails?.inspectionScore;
    const color = carDetails?.vehicleBodyColor || carDetails?.carDetail?.vehicleDetails?.find((d) => d.key === 'Body color')?.value;
    const chassisCode = carDetails?.makerDetail?.type;
    const makerGrade = carDetails?.makerDetail?.grade;

    const params = {};
    if (inspectionScore) params.inspectionScore = inspectionScore;
    if (color) params.color = color;
    if (chassisCode) params.chassisCode = chassisCode;
    if (makerGrade) params.grade = makerGrade;

    setLoadingMarketStats(true);
    getCarMarketStats(params)
      .then((res) => {
        if (res?.message === "Success") {
          setMarketStats(res.data);
        }
      })
      .catch((err) => console.error("Market stats fetch error:", err))
      .finally(() => setLoadingMarketStats(false));
  }, [carDetails?._id, isFreeTier]);

  // Earlier auctions of this car, matched on the chassis number read off the
  // auction sheets. Locked for the free tier, like Market Insights.
  useEffect(() => {
    if (!carDetails?._id || isFreeTier) return;
    let cancelled = false;
    setAuctionHistory(null);
    setLoadingAuctionHistory(true);
    getAuctionHistory(carDetails._id)
      .then((body) => {
        if (!cancelled) setAuctionHistory(body?.data || null);
      })
      .catch((err) => console.error("Auction history fetch error:", err))
      .finally(() => {
        if (!cancelled) setLoadingAuctionHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [carDetails?._id, isFreeTier]);

  // ---------------------------------------------------------------
  // Helper – safely extract a value from carDetails.carDetail.vehicleDetails
  // ---------------------------------------------------------------
  const getVehicleDetailValue = (key) => {
    return carDetails?.carDetail?.vehicleDetails?.find((d) => d.key === key)?.value;
  };

  // Derive overview values with robust fall-backs
  const transmission = carDetails?.vehicleTransmission || carDetails?.engineDetail?.transmission || getVehicleDetailValue('Transmission');
  const color = carDetails?.vehicleBodyColor || getVehicleDetailValue('Body color');
  const mileageRaw = carDetails?.vehicleMileage || carDetails?.mileage || getVehicleDetailValue('Mileage');
  const mileage = mileageRaw ? mileageRaw.replace("(mile)", "") : undefined;
  const displacement = carDetails?.vehicleDisplacement || carDetails?.engineDetail?.displacement || getVehicleDetailValue('Displacement');
  const modelName = carDetails?.vehicleType || carDetails?.makerDetail?.model || getVehicleDetailValue('Type');

  const overviewInformation = [
    { title: "Year", value: carDetails?.model, icon: "fa-calendar-alt" },
    { title: "Make", value: carDetails?.makerDetail?.maker, icon: "fa-car" },
    { title: "Transmission", value: transmission, icon: "fa-cogs" },
    { title: "Inspection Score", value: carDetails?.inspectionScore, icon: "fa-clipboard-check" },
    { title: "Model", value: modelName, icon: "fa-tag" },
    { title: "Color", value: color, icon: "fa-palette" },
    { title: "Mileage", value: mileage, icon: "fa-tachometer-alt" },
    { title: "Displacement", value: displacement, icon: "fa-oil-can" },
  ];

  const handleSendInspectionRequest = async () => {
    if (!userData || !userData.userId) {
      displaySnackbar("Please login to request inspections", "error");
      return;
    }
    
    if (isFreeTier) {
      onOpenUpgradeModal();
      return;
    }
    
    if (!carDetails?._id) {
      displaySnackbar("Car details not available", "error");
      return;
    }
    
    setIsLoadingInspection(true);
    
    try {
      // First create the inspection request
      const inspectionResponse = await requestInspection(carDetails._id);
      
      if (inspectionResponse.message === "Inspection request created successfully") {
        // If that succeeds, send the email notification
        const emailPayload = {
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          bidDetail: carDetails.bidDetail,
          markerDetail: carDetails.makerDetail,
          vehicleImage: carDetails?.vehicleImages?.length > 0 ? carDetails.vehicleImages[0] : '',
          carId: carDetails._id,
          userId: userData.userId
        };
        
        try {
          const emailResponse = await sendInspectionRequest(emailPayload);
          if (emailResponse.message === "Success") {
            displaySnackbar("Inspection Request Sent Successfully", "success");
            // Refresh inspection status and car details
            await fetchUserInspections();
            await handleGetCarDetails(id);
          } else {
            // Still display success even if email fails, since the inspection was created
            displaySnackbar("Inspection request created but notification email failed", "warning");
            await fetchUserInspections();
          }
        } catch (emailError) {
          console.error("Error sending inspection email:", emailError);
          displaySnackbar("Inspection request created but notification email failed", "warning");
          await fetchUserInspections();
        }
      } else if (inspectionResponse.message === "You already have an inspection request for this car") {
        displaySnackbar("You already have an inspection request for this car", "info");
        await fetchUserInspections();
      } else if (inspectionResponse?.upgradeRequired) {
        // The API reads the tier off the member's record, so this browser's is stale.
        onOpenUpgradeModal();
      } else {
        displaySnackbar(inspectionResponse.message || "Error creating inspection request", "error");
      }
    } catch (error) {
      console.error("Error requesting inspection:", error);
      displaySnackbar("Error sending inspection request. Please try again.", "error");
    } finally {
      setIsLoadingInspection(false);
    }
  };

  const handleShowTranslated = async () => {
    if (isFreeTier) {
      onOpenUpgradeModal();
      return;
    }
    
    if (showImages) {
      setShowImages(false);
    }
    setLoadingForTranslatedAuction(true);
    if (!pdfUrl && carDetails?._id) {
      const upgradeRequired = await getTranslatedSheet(carDetails._id);
      if (upgradeRequired) {
        setLoadingForTranslatedAuction(false);
        return;
      }
    }
    setShowTranslated(true);
    setLoadingForTranslatedAuction(false);
    // Scroll to the TranslatedContent component
    if (translatedContentRef.current) {
      translatedContentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // The sheet only renders after handleShowTranslated returns, so scroll to it once it's there
  useEffect(() => {
    if (showTranslated) {
      translatedContentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showTranslated]);

  const getTranslatedSheet = async (id) => {
    try {
      const response = await getTranslatedAuctionSheet({ id });
      if (response?.message === "Success") {
        setPdfUrl(response?.data?.translatedPdf);
      } else if (response?.upgradeRequired) {
        // The API reads the tier off the member's record, so this browser's is stale.
        onOpenUpgradeModal();
        return true;
      } else {
        displaySnackbar("Error fetching translated auction sheet", "error");
      }
    } catch (error) {
      console.error("Error fetching translated auction sheet:", error);
      displaySnackbar("Error fetching translated auction sheet", "error");
    }
  };

  const inspectionGalleryHandler = () => {
    if (showTranslated) {
      setShowTranslated(false);
    }
    setShowImages(true);
    // Scroll to the InspectionGallery component
    if (inspectionGalleryRef.current) {
      inspectionGalleryRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Handler for opening the full-screen image gallery modal
  const handlePictureOverlay = () => {
    // Don't open modal for free tier users if they're viewing a restricted image
    if (isFreeTier && currentImageIndex > 0) {
      onOpenUpgradeModal();
      return;
    }
    
    if (carDetails?.vehicleImages?.length > 0) {
      // Find the index of the current image in the vehicleImages array
      const index = carDetails.vehicleImages.findIndex(img => img === currentImage);
      if (index !== -1) {
        setCurrentImageIndex(index);
        setIsModalOpen(true);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // These count the photos the API held back and go through the thumbnail handler, so a free
  // account stays on the first photo here too
  const goToNext = () => {
    const nextIndex = (currentImageIndex + 1) % totalPhotoCount;
    handleThumbnailClick(carDetails.vehicleImages[nextIndex], nextIndex);
  };

  const goToPrevious = () => {
    const prevIndex = (currentImageIndex - 1 + totalPhotoCount) % totalPhotoCount;
    handleThumbnailClick(carDetails.vehicleImages[prevIndex], prevIndex);
  };

  // Keyboard navigation for image gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up the event listener when component unmounts or modal closes
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, carDetails, currentImageIndex]);

  const handleShare = (e) => {
    if (e) e.stopPropagation();
    const carId = carDetails?._id || id;
    if (!carId) return;
    const shareUrl = `https://api.glojapan.com/share/detail/${carId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      displaySnackbar('Share link copied to clipboard!', 'success');
    }).catch(() => {
      displaySnackbar('Could not copy link', 'error');
    });
  };

  const toggleFavorite = (e) => {
    // Prevent the click from propagating to parent elements
    if (e) {
      e.stopPropagation();
    }
    
    // Make sure we have a valid car ID
    if (!carDetails || !carDetails._id) {
      console.error("Cannot toggle favorite: No car details or car ID available");
      return;
    }
    
    const carId = carDetails._id;
    
    // Store current favorite status before update
    const wasAlreadyFavorite = isFavorite;
    
    // Optimistic UI update (immediately toggle the UI)
    setIsFavorite(!wasAlreadyFavorite);
    
    // Also directly update localStorage for instant persistence
    try {
      const savedIds = localStorage.getItem('favoriteCarIds');
      let favoriteIds = savedIds ? JSON.parse(savedIds) : [];
      
      if (!wasAlreadyFavorite) {
        // Add to favorites
        if (!favoriteIds.includes(carId)) {
          favoriteIds.push(carId);
        }
      } else {
        // Remove from favorites
        favoriteIds = favoriteIds.filter(id => id !== carId);
      }
      
      // Save back to localStorage
      localStorage.setItem('favoriteCarIds', JSON.stringify(favoriteIds));
      console.log(`Updated localStorage favorites: ${!wasAlreadyFavorite ? 'Added' : 'Removed'} ${carId}`);
    } catch (error) {
      console.error("Error updating localStorage favorites:", error);
    }
    
    // Keep track in localFavorites too for consistency
    setLocalFavorites(prev => {
      const newSet = new Set(prev);
      if (wasAlreadyFavorite) {
        newSet.delete(carId);
      } else {
        newSet.add(carId);
      }
      return newSet;
    });

    // Update Redux store for persistence
    dispatch(updateCarDetail({ 
      _id: carId,
      isFavorite: !wasAlreadyFavorite 
    }));

    // API call to update backend
    updateCarFavourite({ carId })
      .then((res) => {
        if (res.status !== 201) {
          // Revert UI on failure
          setIsFavorite(wasAlreadyFavorite);
          
          // Also revert localStorage
          try {
            const savedIds = localStorage.getItem('favoriteCarIds');
            let favoriteIds = savedIds ? JSON.parse(savedIds) : [];
            
            if (wasAlreadyFavorite) {
              // Add back to favorites
              if (!favoriteIds.includes(carId)) {
                favoriteIds.push(carId);
              }
            } else {
              // Remove from favorites
              favoriteIds = favoriteIds.filter(id => id !== carId);
            }
            
            localStorage.setItem('favoriteCarIds', JSON.stringify(favoriteIds));
          } catch (error) {
            console.error("Error reverting localStorage favorites:", error);
          }
          
          // Revert localFavorites too
          setLocalFavorites(prev => {
            const newSet = new Set(prev);
            if (wasAlreadyFavorite) {
              newSet.add(carId);
            } else {
              newSet.delete(carId);
            }
            return newSet;
          });
          
          // Revert Redux store too
          dispatch(updateCarDetail({ 
            _id: carId,
            isFavorite: wasAlreadyFavorite
          }));
          
          displaySnackbar("Could not update favorite status", "error");
        } else {
          displaySnackbar(wasAlreadyFavorite ? "Removed from favorites" : "Added to favorites", "success");
        }
      })
      .catch((error) => {
        console.error("Error updating favorite status:", error);
        
        // Revert UI on error
        setIsFavorite(wasAlreadyFavorite);
        
        // Also revert localStorage
        try {
          const savedIds = localStorage.getItem('favoriteCarIds');
          let favoriteIds = savedIds ? JSON.parse(savedIds) : [];
          
          if (wasAlreadyFavorite) {
            // Add back to favorites
            if (!favoriteIds.includes(carId)) {
              favoriteIds.push(carId);
            }
          } else {
            // Remove from favorites
            favoriteIds = favoriteIds.filter(id => id !== carId);
          }
          
          localStorage.setItem('favoriteCarIds', JSON.stringify(favoriteIds));
        } catch (error) {
          console.error("Error reverting localStorage favorites:", error);
        }
        
        // Revert localFavorites too
        setLocalFavorites(prev => {
          const newSet = new Set(prev);
          if (wasAlreadyFavorite) {
            newSet.add(carId);
          } else {
            newSet.delete(carId);
          }
          return newSet;
        });
        
        // Revert Redux store too
        dispatch(updateCarDetail({ 
          _id: carId,
          isFavorite: wasAlreadyFavorite
        }));
        
        displaySnackbar("Could not update favorite status", "error");
      });
  };

  const getInspectionButtonClass = () => {
    // For free tier users, just return the base class without status styling
    if (isFreeTier) {
      return classes.inspectionBtn;
    }

    // For VIP users, keep the existing status-based styling
    const status = inspection?.status?.toLowerCase() || '';
    
    if (status === 'pending') {
      return `${classes.inspectionBtn} ${classes.pendingBtn}`;
    } else if (status === 'processing') {
      return `${classes.inspectionBtn} ${classes.processingBtn}`;
    } else if (status === 'completed') {
      return `${classes.inspectionBtn} ${classes.completedBtn}`;
    } else if (status === 'cancelled') {
      return `${classes.inspectionBtn} ${classes.cancelledBtn}`;
    } else if (status === 'submitted') {
      return `${classes.inspectionBtn} ${classes.pendingBtn}`;
    }
    
    return classes.inspectionBtn;
  };

  const getInspectionButtonText = () => {
    const status = inspection?.status?.toLowerCase() || '';
    
    if (status === 'pending' || status === 'submitted') {
      return "Inspection Request Pending";
    } else if (status === 'processing') {
      return "Inspection In Progress";
    } else if (status === 'completed') {
      return "View Inspection Photos";
    } else if (status === 'cancelled') {
      return "Request New Inspection";
    }
    
    return "Request Inspection";
  };

  const isInspectionButtonDisabled = () => {
    if (isLoadingInspection) return true;
    
    const status = inspection?.status?.toLowerCase() || '';
    return ['pending', 'submitted', 'processing'].includes(status);
  };

  const handleInspectionButtonClick = () => {
    const status = inspection?.status?.toLowerCase() || '';
    
    if (status === 'completed') {
      // If completed, show the inspection gallery
      inspectionGalleryHandler();
    } else if (status === 'pending' || status === 'in-progress') {
      // If already pending or in-progress, don't allow re-requesting
      displaySnackbar("You already have an inspection request for this vehicle", "info");
    } else {
      // For all other cases (new request, cancelled, etc.), show confirmation
      setShowInspectionConfirmation(true);
    }
  };

  const confirmInspectionRequest = () => {
    setShowInspectionConfirmation(false);
    handleSendInspectionRequest();
  };

  const cancelInspectionRequest = () => {
    setShowInspectionConfirmation(false);
  };

  // Add a function to fetch user's existing bids for this car
  const fetchUserBids = useCallback(async () => {
    if (!carDetails?._id || !userData?.userId) return;
    
    try {
      const response = await getUserBids(carDetails._id);
      if (response.data && response.data.length > 0) {
        // Sort by amount to get the highest bid
        const bids = response.data.sort((a, b) => (b.bidAmount || 0) - (a.bidAmount || 0));
        const highestBid = bids[0];
        setExistingBid(highestBid);
        setMinimumBid(highestBid.bidAmount || 0);
        setBidPlaced(true);
      } else {
        setExistingBid(null);
        setBidPlaced(false);
        setMinimumBid(0);
      }
    } catch (error) {
      console.error('Error fetching user bids:', error);
      displaySnackbar("Failed to fetch your existing bids", "error");
      // Ensure defaults are set in case of error
      setExistingBid(null);
      setBidPlaced(false);
      setMinimumBid(0);
    }
  }, [carDetails?._id, userData?.userId, displaySnackbar]);

  // Call fetchUserBids when component mounts or carDetails changes
  useEffect(() => {
    if (carDetails?._id) {
      fetchUserBids();
    }
  }, [carDetails, fetchUserBids]);

  // Modify your image handling functions to handle free tier restrictions
  const handleThumbnailClick = (image, index) => {
    // For free tier users, only allow access to the first image. A photo the API
    // held back has no URL to show, whatever tier the browser thinks this is.
    if ((isFreeTier && index > 0) || !image) {
      onOpenUpgradeModal();
      return;
    }
    
    setCurrentImage(image);
    setCurrentImageIndex(index);
  };

  // Add this new function to determine if a thumbnail should be blurred
  const shouldBlurThumbnail = (index) => {
    return isFreeTier && index > 0;
  };

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };

  // Function to download all car images
  const handleDownloadAllImages = async () => {
    if (!carDetails?.vehicleImages || carDetails.vehicleImages.length === 0) {
      displaySnackbar("No images available to download", "error");
      return;
    }
    
    setIsDownloading(true);
    
    try {
      // Generate a car name for the filenames
      const carName = `${carDetails?.model || 'car'}_${carDetails?.makerDetail?.maker || ''}_${carDetails?.makerDetail?.model || ''}_${carDetails?.makerDetail?.grade || ''}`.replace(/\s+/g, '_');
      
      // Check if mobile device
      if (isMobileDevice()) {
        // For mobile devices, create a ZIP file on the server
        try {
          const response = await axiosInterceptors().post(baseURL + "/cars/download-images-zip", {
            carId: carDetails._id,
            imageUrls: carDetails.vehicleImages,
            carName: carName
          });
          
          if (response.success && response.data?.zipUrl) {
            // Download the ZIP file
            const link = document.createElement('a');
            link.href = response.data.zipUrl;
            link.download = `${carName}_images.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            displaySnackbar(`Successfully created ZIP file with ${carDetails.vehicleImages.length} images`, "success");
          } else {
            throw new Error(response.message || "Failed to create ZIP file");
          }
        } catch (error) {
          console.error("Error creating ZIP file:", error);
          displaySnackbar("Failed to create ZIP file. Try downloading images individually.", "error");
          
          // Fallback to sequential download for mobile
          await downloadImagesSequentially(carName);
        }
      } else {
        // For desktop, use the existing sequential download method
        await downloadImagesSequentially(carName);
      }
    } catch (error) {
      console.error("Error downloading images:", error);
      displaySnackbar("Error downloading images", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper function for sequential image downloads
  const downloadImagesSequentially = async (carName) => {
    const downloadImage = async (url, filename) => {
      try {
        // Fetch the image as a blob
        const response = await fetch(url);
        const blob = await response.blob();
        
        // Create object URL for the blob
        const objectUrl = URL.createObjectURL(blob);
        
        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        
        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        URL.revokeObjectURL(objectUrl);
        
        return true;
      } catch (error) {
        console.error(`Error downloading image ${filename}:`, error);
        return false;
      }
    };

    let successCount = 0;
    const isMobile = isMobileDevice();
    
    for (let i = 0; i < carDetails.vehicleImages.length; i++) {
      const imageUrl = carDetails.vehicleImages[i];
      const filename = `${carName}_image_${i + 1}.jpg`;
      
      const success = await downloadImage(imageUrl, filename);
      if (success) successCount++;
      
      // Add longer delay for mobile devices to prevent cancellation
      if (i < carDetails.vehicleImages.length - 1) {
        await new Promise(resolve => setTimeout(resolve, isMobile ? 1000 : 300));
      }
    }
    
    if (successCount === carDetails.vehicleImages.length) {
      displaySnackbar(`Successfully downloaded all ${successCount} images`, "success");
    } else if (successCount > 0) {
      displaySnackbar(`Downloaded ${successCount} of ${carDetails.vehicleImages.length} images`, "warning");
    } else {
      displaySnackbar("Failed to download images", "error");
    }
  };

  // Show loading state
  if (loadingForCarDetails) {
    return (
      <div className={classes.loadingWrapper}>
        <div className={classes.loader}></div>
        <p>Loading car details...</p>
      </div>
    );
  }

  // Graceful not-found / error state.
  // The error path sets carDetails to null; render a friendly message instead of
  // an empty/broken detail page.
  if (!carDetails || !carDetails._id) {
    return (
      <div className={classes.loadingWrapper}>
        <i className="fas fa-exclamation-circle" style={{ fontSize: '32px', color: '#667085', marginBottom: '12px' }}></i>
        <p>Car details not found.</p>
        <button
          className={classes.backButton}
          onClick={handleBackClick}
          aria-label="Go back"
          style={{ marginTop: '12px' }}
        >
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
      </div>
    );
  }

  // Once the auction is over the page is a record of the car (the banner under
  // the title says so): no starting bid, no bid form and no new inspection
  // requests. An inspection already made stays, so its photos can be opened.
  const auctionEnded = isAuctionOver(carDetails);
  const inspectionStatus = inspection?.status?.toLowerCase() || '';
  const showInspection = !auctionEnded || (inspectionStatus !== '' && inspectionStatus !== 'cancelled');

  return (
    <>
      <div className={classes.detailContentWrapper}>
        <div className={classes.detailHeader}>
          <button 
            className={classes.backButton} 
            onClick={handleBackClick}
            aria-label="Go back"
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
          <button 
            className={`${classes.favoriteButton} ${isFavorite ? classes.active : ''}`}
            onClick={(e) => toggleFavorite(e)}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <i className={`${isFavorite ? 'fas' : 'far'} fa-heart`}></i>
          </button>
          <button
            className={classes.shareButton}
            onClick={handleShare}
            aria-label="Copy share link"
            title="Copy share link"
          >
            <i className="fas fa-share-nodes"></i>
          </button>
        </div>

        <h1 className={classes.carTitle}>
          {carDetails?.model} {carDetails?.makerDetail?.maker} {carDetails?.makerDetail?.model} {carDetails?.makerDetail?.grade}
        </h1>

        <EndedAuctionBanner car={carDetails} userId={userData?.userId} />

        <div className={classes.twoColumnLayout}>
          {/* Left column - Car Images */}
          <div className={classes.detailsColumn}>
            {/* Image Gallery */}
            <div className={classes.imageSection}>
              {/* Admin download images button */}
              {isAdmin && carDetails?.vehicleImages?.length > 0 && (
                <div className={classes.adminActions}>
                  <button 
                    className={classes.downloadButton}
                    onClick={handleDownloadAllImages}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Downloading...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download"></i>
                        <span>Download All Images</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              
              <div className={classes.mainImageWrapper}>
                <img
                  src={currentImage}
                  alt={carDetails.model}
                  className={classes.mainImage}
                  loading="lazy"
                  onClick={handlePictureOverlay}
                  style={{ cursor: 'pointer' }}
                />
                
                {/* Add zoom icon to indicate image can be enlarged */}
                <div className={classes.zoomIcon} onClick={handlePictureOverlay}>
                  <i className="fas fa-search-plus"></i>
                </div>
                
                {/* Add VIP upgrade overlay for additional images if free tier */}
                {isFreeTier && currentImageIndex > 0 && (
                  <div className={classes.vipImageOverlay} onClick={onOpenUpgradeModal}>
                    <div className={classes.overlayContent}>
                      <i className="fas fa-lock"></i>
                      <p>Upgrade to see all photos</p>
                      <button className={classes.upgradeBtn}>Upgrade Now</button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={classes.thumbnailContainer}>
                {carDetails?.vehicleImages?.map((image, index) => (
                  <div
                    key={index}
                    className={`${classes.thumbnail} ${
                      currentImage === image ? classes.activeThumbnail : ''
                    } ${shouldBlurThumbnail(index) ? classes.blurredThumbnail : ''}`}
                    onClick={() => handleThumbnailClick(image, index)}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} loading="lazy" />
                    
                    {/* Add lock icon on blurred thumbnails */}
                    {shouldBlurThumbnail(index) && (
                      <div className={classes.thumbnailLock}>
                        <i className="fas fa-lock"></i>
                      </div>
                    )}
                  </div>
                ))}

                {/* The API sends a free account only the first photo, plus how many there are */}
                {Array.from({ length: lockedPhotoCount }, (_, index) => (
                  <div
                    key={`locked-${index}`}
                    className={`${classes.thumbnail} ${classes.blurredThumbnail}`}
                    onClick={onOpenUpgradeModal}
                  >
                    <img src={carDetails.vehicleImages[0]} alt="Locked" loading="lazy" />
                    <div className={classes.thumbnailLock}>
                      <i className="fas fa-lock"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Market Insights Module */}
            {isFreeTier ? (
              <div className={classes.vipFeatureContainer} onClick={onOpenUpgradeModal}>
                <div className={classes.marketInsightsContainer}>
                  <h3 className={classes.marketInsightsTitle}>
                    <i className="fas fa-chart-bar"></i>
                    Market Insights
                    <span className={classes.vipBadge}>
                      <i className="fas fa-lock"></i> VIP
                    </span>
                  </h3>
                  <div className={classes.estimatorContentLocked}>
                    <p>Upgrade to see how this car compares to others in the market.</p>
                    <button className={classes.upgradeBtn}>Upgrade to Access</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className={classes.marketInsightsContainer}>
                <h3 className={classes.marketInsightsTitle}>
                  <i className="fas fa-chart-bar"></i>
                  Market Insights
                </h3>
                {loadingMarketStats ? (
                  <div className={classes.marketInsightsLoading}>
                    <div className={classes.marketInsightsSpinner}></div>
                    <span>Loading market data…</span>
                  </div>
                ) : marketStats ? (
                  <div className={classes.marketInsightsGrid}>
                    <div className={classes.marketInsightsCard}>
                      <div className={classes.marketInsightsIcon}>
                        <i className="fas fa-star"></i>
                      </div>
                      <div className={classes.marketInsightsValue}>
                        {marketStats.totalActive > 0
                          ? `${Math.round((marketStats.gradeCount / marketStats.totalActive) * 100)}%`
                          : '—'}
                      </div>
                      <div className={classes.marketInsightsLabel}>
                        Grade {carDetails?.inspectionScore || '—'} Listings
                      </div>
                      <div className={classes.marketInsightsContext}>
                        of all recorded sales
                      </div>
                    </div>
                    <div className={classes.marketInsightsCard}>
                      <div className={classes.marketInsightsIcon}>
                        <i className="fas fa-palette"></i>
                      </div>
                      <div className={classes.marketInsightsValue}>
                        {marketStats.totalActive > 0
                          ? `${Math.round((marketStats.colorCount / marketStats.totalActive) * 100)}%`
                          : '—'}
                      </div>
                      <div className={classes.marketInsightsLabel}>
                        {carDetails?.vehicleBodyColor
                          || carDetails?.carDetail?.vehicleDetails?.find((d) => d.key === 'Body color')?.value
                          || '—'} Exterior
                      </div>
                      <div className={classes.marketInsightsContext}>
                        of all recorded sales
                      </div>
                    </div>
                    <div className={classes.marketInsightsCard}>
                      <div className={classes.marketInsightsIcon}>
                        <i className="fas fa-layer-group"></i>
                      </div>
                      <div className={classes.marketInsightsValue}>
                        {marketStats.trimPercent != null ? `${marketStats.trimPercent}%` : '—'}
                      </div>
                      <div className={classes.marketInsightsLabel}>
                        {marketStats.filters?.chassisCode
                          ? marketStats.filters.chassisCode.normalize('NFKC')
                          : ''} {marketStats.filters?.grade || ''}
                      </div>
                      <div className={classes.marketInsightsContext}>
                        of {marketStats.filters?.chassisCode
                          ? marketStats.filters.chassisCode.normalize('NFKC')
                          : ''} sales
                      </div>
                    </div>
                    <div className={classes.marketInsightsCard}>
                      <div className={classes.marketInsightsIcon}>
                        <i className="fas fa-tachometer-alt"></i>
                      </div>
                      <div className={classes.marketInsightsValue}>
                        {marketStats.avgMileage != null
                          ? `${marketStats.avgMileage.toLocaleString()} km`
                          : '—'}
                      </div>
                      <div className={classes.marketInsightsLabel}>
                        Average Mileage
                      </div>
                      <div className={classes.marketInsightsContext}>
                        {marketStats.filters?.chassisCode
                          ? marketStats.filters.chassisCode.normalize('NFKC')
                          : ''}{marketStats.filters?.grade ? ' ' + marketStats.filters.grade : ''}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className={classes.marketInsightsEmpty}>No market data available.</p>
                )}
              </div>
            )}

            {/* Production date (month of manufacture) lookup by chassis number */}
            {isFreeTier ? (
              <div className={classes.vipFeatureContainer} onClick={onOpenUpgradeModal}>
                <div className={classes.estimatorContainerCompact}>
                  <h3 className={classes.estimatorTitle}>
                    <i className="fas fa-industry"></i>
                    Production Date
                    <span className={classes.vipBadge}>
                      <i className="fas fa-lock"></i> VIP
                    </span>
                  </h3>
                  <div className={classes.estimatorContentLocked}>
                    <p>Upgrade to check the month this car was built.</p>
                    <button className={classes.upgradeBtn}>Upgrade to Access</button>
                  </div>
                </div>
              </div>
            ) : (
              <ProductionDateCard
                maker={carDetails?.makerDetail?.maker}
                chassisCode={carDetails?.makerDetail?.type}
                chassisNo={auctionHistory?.status === 'verified' ? auctionHistory.chassisNo : ''}
                onUpgradeRequired={onOpenUpgradeModal}
              />
            )}
          </div>

          {/* Right column - Overview and Bidding */}
          <div className={classes.biddingSidebar}>
            {/* Car Details */}
            <div className={classes.infoSection}>
              <VIPFeatureIndicator feature="auction-time">
                <div className={classes.auctionDateTimeSection}>
                  <h2 className={classes.sectionTitle}>
                    <i className="far fa-clock"></i>
                    Auction Date & Time
                  </h2>
                  <div className={classes.auctionTimeGrid}>
                    <div className={classes.auctionDateTimeItem}>
                      <div className={classes.auctionDateTimeIcon}>
                        <i className="fas fa-gavel"></i>
                      </div>
                      <div className={classes.auctionDateTimeInfo}>
                        <span>{carDetails?.bidDetail?.bidSite ? `${carDetails.bidDetail.bidSite} Bid No.` : "Bid No."}</span>
                        <strong>{carDetails?.bidNo || "N/A"}</strong>
                      </div>
                    </div>
                    <div className={classes.auctionDateTimeItem}>
                      <div className={classes.auctionDateTimeIcon}>
                        <i className="far fa-calendar-alt"></i>
                      </div>
                      <div className={classes.auctionDateTimeInfo}>
                        <span>Bidding Date:</span>
                        <strong>
                          {carDetails?.bidDetail?.bidDate ?
                            `${new Date(carDetails?.bidDetail?.bidDate).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })} JST` : "N/A"}
                        </strong>
                      </div>
                    </div>
                    <div className={classes.auctionDateTimeItem}>
                      <div className={classes.auctionDateTimeIcon}>
                        <i className="far fa-clock"></i>
                      </div>
                      <div className={classes.auctionDateTimeInfo}>
                        <span>Auction Time:</span>
                        <strong>
                          {isFreeTier ? 'VIP Feature' : (carDetails?.auctionTime ? `${carDetails.auctionTime} JST` : 'N/A')}
                        </strong>
                      </div>
                    </div>
                    <div className={classes.auctionDateTimeItem}>
                      <div className={classes.auctionDateTimeIcon}>
                        <i className="fas fa-globe"></i>
                      </div>
                      <div className={classes.auctionDateTimeInfo}>
                        <span>Your Local Time:</span>
                        <strong>
                          {isFreeTier ? 'VIP Feature' : 
                            (carDetails?.bidDetail?.bidDate && carDetails?.auctionTime ?
                              (() => {
                                try {
                                  const [year, month, day] = carDetails.bidDetail.bidDate.split('/').map(Number);

                                  const timeStr = carDetails.auctionTime;
                                  const timeParts = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                                  if (!timeParts) return "Time not available";
                                  let hour = parseInt(timeParts[1], 10);
                                  const minute = parseInt(timeParts[2], 10);
                                  if (timeParts?.[3]) {
                                    const period = timeParts[3].toUpperCase();
                                    if (period === 'PM' && hour !== 12) hour += 12;
                                    if (period === 'AM' && hour === 12) hour = 0;
                                  }

                                  const jstDateTime = DateTime.fromObject({
                                    year, month, day, hour, minute
                                  }, { zone: 'Asia/Tokyo' });

                                  if (!jstDateTime.isValid) return "Time not available";

                                  const localDateTime = jstDateTime.toLocal();
                                  return localDateTime.toLocaleString({
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    timeZoneName: 'short'
                                  });
                                } catch {
                                  return "Time not available";
                                }
                              })() 
                              : "Time not available")
                          }
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </VIPFeatureIndicator>
              
              <div className={classes.sectionDivider}></div>
              
              <div className={classes.overviewSection}>
                <h2 className={classes.sectionTitle}>
                  <i className="fas fa-info-circle"></i>
                  Overview
                </h2>
                <div className={classes.overviewGrid}>
                  {overviewInformation.map(({title, value, icon}) => (
                    <div key={title} className={classes.overviewItem}>
                      <div className={classes.overviewIcon}>
                        <i className={`fas ${icon}`}></i>
                      </div>
                      <div className={classes.overviewContent}>
                        <span className={classes.overviewLabel}>{title}</span>
                        <span className={classes.overviewValue}>{value || 'N/A'}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

              {/*
                Starting Bid is a VIP+ / admin field. The server withholds the
                value from lower tiers, and the section is not rendered for
                them either - no heading, no lock, no upgrade prompt - so a
                free/VIP account never learns the field exists. Entitled users
                get the value, or "N/A" when the record genuinely has none.
              */}
              {canSeeStartingBid && !auctionEnded && (
                <>
                  <div className={classes.sectionDivider}></div>
                  <div className={classes.overviewSection}>
                    <h2 className={classes.sectionTitle}>
                      <i className="fas fa-tag"></i>
                      Starting Bid
                    </h2>
                    <div className={classes.overviewGrid}>
                      <div className={classes.overviewItem}>
                        <div className={classes.overviewIcon}>
                          <i className="fas fa-yen-sign"></i>
                        </div>
                        <div className={classes.overviewContent}>
                          <span className={classes.overviewLabel}>Starting Bid</span>
                          <span className={classes.overviewValue}>
                            {startingBid || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Bidding Section - Collapsible. Gone once the auction is over;
                  the API refuses a bid then anyway. */}
              {!auctionEnded && (
              <>
              <div className={classes.sectionDivider}></div>

              <VIPFeatureIndicator feature="bidding" isDisabled={true}>
                <div className={`${classes.biddingSection}`}>
                  <h2 className={classes.sectionTitle}>
                    <i className="fas fa-gavel"></i>
                    Place Your Bid
                  </h2>
                  {!isBiddingExpanded ? (
                    // A free account's click carries on to the VIPFeatureIndicator around this
                    // section, which opens the upgrade window; opening one here as well stacked two.
                    <button 
                      className={`${classes.expandBidButton} ${isFreeTier ? classes.vipFeatureButton : ''}`}
                      onClick={isFreeTier ? undefined : toggleBiddingSection}
                      aria-label="Expand bidding section"
                    >
                      <span>Place Bid</span>
                      {isFreeTier ? (
                        <i className="fas fa-lock"></i>
                      ) : (
                        <i className="fas fa-chevron-down"></i>
                      )}
                    </button>
                  ) : (
                    <div className={classes.biddingContainer}>
                      <div className={classes.bidInputContainer}>
                        <label htmlFor="bidAmount" className={classes.bidLabel}>
                          {bidPlaced ? "Increase Bid Amount ($)" : "Bid Amount ($)"}
                          {bidPlaced && (
                            <span className={classes.minBidNote}>
                              (min: ${(minimumBid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                            </span>
                          )}
                        </label>
                        <div className={`${classes.bidInputWrapper} ${bidError ? classes.error : ''}`}>
                          <span className={classes.currencySymbol}>$</span>
                          <input
                            id="bidAmount"
                            type="text"
                            className={classes.bidInput}
                            value={bidAmount}
                            onChange={handleBidChange}
                            placeholder={bidPlaced ? `Enter amount higher than ${(minimumBid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Enter your bid amount"}
                            disabled={isPlacingBid}
                          />
                        </div>
                        {bidError && <div className={classes.bidError}>{bidError}</div>}
                        
                        {/* Add bid limit information */}
                        <div className={classes.bidLimitInfo}>
                          <div className={classes.bidLimitHeader}>
                            <i className="fas fa-info-circle"></i>
                            <span>Your maximum bid power: ${((userData?.deposit?.amount || 0) * 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className={classes.bidLimitDetails}>
                            This is 10x your deposit amount of <strong>${(userData?.deposit?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>. Contact support to increase your limit.
                          </div>
                        </div>
                      </div>
                      
                      <div className={classes.priceCalculations}>
                        <div className={classes.priceRow}>
                          <span className={classes.priceLabel}>Estimated Total (Shipped):</span>
                          <span className={classes.priceValue}>
                            ${(shippedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button 
                            className={classes.detailsToggle} 
                            onClick={toggleShippedDetails}
                            aria-label={showShippedDetails ? "Hide shipping details" : "Show shipping details"}
                          >
                            <i className={`fas fa-chevron-${showShippedDetails ? 'up' : 'down'}`}></i>
                          </button>
                        </div>
                        
                        {showShippedDetails && (
                          <div className={classes.priceDetails}>
                            <div className={classes.priceDetailRow}>
                              <span>Bid Amount:</span>
                              <span>${parseFloat(bidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Freight Shipping:</span>
                              <span>${(FREIGHT_SHIPPING || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Japan Inland Transport:</span>
                              <span>${(JAPAN_INLAND_TRANSPORT || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Export Fee:</span>
                              <span>${(EXPORT_FEE || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Magic Motorsports Fee:</span>
                              <span>${(MAGIC_MOTORSPORTS_FEE || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        )}
                        
                        <div className={classes.priceRow}>
                          <span className={classes.priceLabel}>Estimated Total (Imported):</span>
                          <span className={classes.priceValue}>
                            ${(importedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button 
                            className={classes.detailsToggle} 
                            onClick={toggleImportedDetails}
                            aria-label={showImportedDetails ? "Hide import details" : "Show import details"}
                          >
                            <i className={`fas fa-chevron-${showImportedDetails ? 'up' : 'down'}`}></i>
                          </button>
                        </div>
                        
                        {showImportedDetails && (
                          <div className={classes.priceDetails}>
                            <div className={classes.priceDetailRow}>
                              <span>Shipped Total:</span>
                              <span>${(shippedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>US Import Tariff (12.5%):</span>
                              <span>${((parseFloat(bidAmount || 0) * US_IMPORT_TARIFF_RATE) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Harbor Maintenance Fee (0.0125%):</span>
                              <span>${((parseFloat(bidAmount || 0) * HARBOR_MAINTENANCE_FEE_RATE) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Processing Fee (0.346%):</span>
                              <span>${((parseFloat(bidAmount || 0) * PROCESSING_FEE_RATE) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Terminal Handling Fee:</span>
                              <span>${(TERMINAL_HANDLING_FEE || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className={classes.priceDetailRow}>
                              <span>Import Fee:</span>
                              <span>${(IMPORT_FEE || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <button 
                        className={`${classes.placeBidButton} ${isFreeTier ? classes.vipFeatureButton : ''}`}
                        onClick={handlePlaceBid}
                        disabled={isPlacingBid}
                        aria-label="Place bid"
                      >
                        {isPlacingBid ? (
                          <span>
                            <i className="fas fa-spinner fa-spin"></i> Processing...
                          </span>
                        ) : isFreeTier ? (
                          <span>
                            <i className="fas fa-lock"></i> VIP Feature
                          </span>
                        ) : bidPlaced ? (
                          <span>
                            {minimumBid > 0 ? "Increase Bid" : "Place Bid"}
                          </span>
                        ) : (
                          <span>Place Bid</span>
                        )}
                      </button>
                      
                      <button 
                        className={classes.collapseBidButton}
                        onClick={toggleBiddingSection}
                        aria-label="Collapse bidding section"
                      >
                        <span>Collapse <i className="fas fa-chevron-up"></i></span>
                      </button>
                    </div>
                  )}
                </div>
              </VIPFeatureIndicator>
              </>
              )}

              {showInspection && (
              <>
              <div className={classes.sectionDivider}></div>

              {/* Inspection Request Section. As with the bid button, a free account's click carries on to the VIPFeatureIndicator */}
              <VIPFeatureIndicator feature="inspection" isDisabled={true}>
                <div className={classes.inspectionRequestSection}>
                  <h2 className={classes.sectionTitle}>
                    <i className="fas fa-clipboard-check"></i>
                    Inspection
                  </h2>
                  <button 
                    className={`${getInspectionButtonClass()} ${isFreeTier ? classes.vipFeatureButton : ''}`}
                    onClick={isFreeTier ? undefined : handleInspectionButtonClick}
                    disabled={!isFreeTier && isInspectionButtonDisabled()}
                  >
                    {isLoadingInspection ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        {isFreeTier ? (
                          <i className="fas fa-lock"></i>
                        ) : inspection?.status?.toLowerCase() === 'completed' ? (
                          <i className="fas fa-images"></i>
                        ) : (
                          <i className="fas fa-clipboard-check"></i>
                        )}
                        <span>{isFreeTier ? "VIP Feature" : getInspectionButtonText()}</span>
                      </>
                    )}
                  </button>
                </div>
              </VIPFeatureIndicator>
              </>
              )}

              {/* Action buttons moved inside the sidebar */}
              <div className={classes.actionButtonsContainer}>
                <VIPFeatureIndicator feature="auction-sheet" isDisabled={true}>
                  <button 
                    className={`${classes.translateBtn} ${isFreeTier ? classes.vipFeatureButton : ''}`}
                    onClick={isFreeTier ? undefined : handleShowTranslated}
                    disabled={!isFreeTier && loadingForTranslatedAuction}
                  >
                    {isFreeTier ? (
                      <i className="fas fa-lock"></i>
                    ) : loadingForTranslatedAuction ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-file-alt"></i>
                    )}
                    <span>
                      {isFreeTier ? "VIP Feature" : 
                       loadingForTranslatedAuction ? "Processing..." : 
                       "See translated auction sheet"}
                    </span>
                  </button>
                </VIPFeatureIndicator>
                
                <button 
                  className={classes.infoLink}
                  aria-label="Learn how to read an auction sheet"
                  onClick={() => window.open('/guide', '_blank')}
                >
                  <i className="fas fa-info-circle"></i>
                  <span>Learn how to read an auction sheet</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Earlier auctions of this car, matched on the sheet's chassis number.
            Full width under the two columns rather than inside one: it runs
            from one line to a table, and the columns stretch to match each
            other, so inside a column it left a gap in the other. */}
        <div className={classes.historySection}>
          {isFreeTier ? (
            <div className={classes.vipFeatureContainer} onClick={onOpenUpgradeModal}>
              <div className={classes.estimatorContainerCompact}>
                <h3 className={classes.estimatorTitle}>
                  <i className="fas fa-history"></i>
                  Auction History
                  <span className={classes.vipBadge}>
                    <i className="fas fa-lock"></i> VIP
                  </span>
                </h3>
                <div className={classes.estimatorContentLocked}>
                  <p>Upgrade to see whether this car has been through auction before.</p>
                  <button className={classes.upgradeBtn}>Upgrade to Access</button>
                </div>
              </div>
            </div>
          ) : (
            <AuctionHistoryCard history={auctionHistory} loading={loadingAuctionHistory} />
          )}
        </div>

        {/* Bid Confirmation Modal */}
        {showBidConfirmation && (
          <div className={classes.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="bid-confirmation-title">
            <div className={classes.confirmationModal}>
              <h3 id="bid-confirmation-title">Confirm Bid</h3>
              <p>Are you sure you want to place a bid of <strong>${parseFloat(bidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> on this vehicle?</p>
              
              <div className={classes.confirmationDetails}>
                <div className={classes.confirmationRow}>
                  <span>Estimated Total (Shipped):</span>
                  <span>${(shippedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className={classes.confirmationRow}>
                  <span>Estimated Total (Imported):</span>
                  <span>${(importedPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
              
              <div className={classes.confirmationActions}>
                <button 
                  className={classes.cancelButton}
                  onClick={cancelBidConfirmation}
                  type="button"
                  aria-label="Cancel bid"
                >
                  Cancel
                </button>
                <button 
                  className={classes.confirmButton}
                  onClick={confirmBid}
                  type="button"
                  aria-label="Confirm bid"
                >
                  Confirm Bid
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inspection Request Confirmation Modal */}
        {showInspectionConfirmation && (
          <div className={classes.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="inspection-confirmation-title">
            <div className={classes.confirmationModal}>
              <h3 id="inspection-confirmation-title">
                <i className="fas fa-clipboard-check"></i> Confirm Inspection Request
              </h3>
              <p>Are you sure you want to request an inspection for this vehicle?</p>
              
              <div className={classes.confirmationDetails}>
                <div className={classes.vehicleInfo}>
                  <p><strong>{carDetails?.makerDetail?.maker} {carDetails?.makerDetail?.model}</strong></p>
                  <p>Year: {carDetails?.model} | Grade: {carDetails?.inspectionScore || 'N/A'}</p>
                  <p>Auction: {carDetails?.bidDetail?.bidSite} | Bid No: {carDetails?.bidDetail?.bidNo}</p>
                </div>
                <div className={classes.inspectionNote}>
                  <i className="fas fa-info-circle"></i>
                  <span>Our team will inspect the vehicle and provide you with detailed photos and a comprehensive report.</span>
                </div>
              </div>
              
              <div className={classes.confirmationActions}>
                <button 
                  className={classes.cancelButton}
                  onClick={cancelInspectionRequest}
                  type="button"
                  aria-label="Cancel inspection request"
                >
                  Cancel
                </button>
                <button 
                  className={classes.confirmButton}
                  onClick={confirmInspectionRequest}
                  type="button"
                  aria-label="Confirm inspection request"
                >
                  <i className="fas fa-check"></i> Confirm Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showTranslated && (
        <div ref={translatedContentRef} className={classes.translatedSheet}>
          <TranslatedContent
            loadingForTranslatedAuction={loadingForTranslatedAuction}
            pdfUrl={pdfUrl}
            bidNo={carDetails?.bidNo}
            handleShowTranslated={() => setShowTranslated(false)}
          />
        </div>
      )}

      {showImages && (
        <div ref={inspectionGalleryRef}>
          <InspectionGallery
            images={[...(inspection?.images || []), ...(inspection?.videos || [])]}
            notes={inspection?.notes}
            onClose={() => setShowImages(false)}
          />
        </div>
      )}

      {/* Modal for viewing larger images */}
      {isModalOpen && carDetails?.vehicleImages?.length > 0 && (
        <div className={classes.imageModal} onClick={closeModal}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={classes.closeButton} onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className={classes.modalImageWrapper}>
              <img 
                src={carDetails.vehicleImages[currentImageIndex]} 
                alt={`${carDetails?.model} ${carDetails?.makerDetail?.maker}`} 
                className={classes.modalImage}
              />
            </div>
            
            <div className={classes.navigationButtons}>
              <button className={classes.navButton} onClick={goToPrevious}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className={classes.imageCounter}>
                {currentImageIndex + 1} / {totalPhotoCount}
              </span>
              <button className={classes.navButton} onClick={goToNext}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIP Photos Upgrade Modal */}
      <UpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={onCloseUpgradeModal} 
        feature="photos" 
      />
    </>
  );
}

export default DetailContent;


